import React, { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import "./ProductPage.css";
import { DownloadIcon } from "../components/ActionIcons";



import allProducts from "./productsData.json";


const PLACEHOLDER_THUMB = `data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='80' height='80' viewBox='0 0 80 80'><rect width='80' height='80' rx='8' fill='%23ecfdf5'/><rect x='20' y='24' width='40' height='28' rx='4' fill='none' stroke='%23065f46' stroke-width='2' opacity='0.4'/><circle cx='30' cy='35' r='5' fill='%23065f46' opacity='0.2'/><polyline points='20,52 32,38 44,46 56,34 60,38 60,52' fill='%23065f46' opacity='0.15'/></svg>`;
const PLACEHOLDER_MAIN  = `data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='600' height='450' viewBox='0 0 600 450'><rect width='600' height='450' fill='%23f0fdf4'/><rect x='160' y='110' width='280' height='200' rx='16' fill='none' stroke='%23065f46' stroke-width='2.5' opacity='0.3'/><circle cx='220' cy='165' r='26' fill='%23065f46' opacity='0.12'/><polyline points='160,310 230,230 295,272 370,195 445,238 445,310' fill='none' stroke='%23065f46' stroke-width='2' opacity='0.15'/><text x='300' y='370' text-anchor='middle' font-family='Inter,sans-serif' font-size='13' fill='%23065f46' opacity='0.45'>Image not found</text></svg>`;


const svgIcons = {
  display: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="4" width="18" height="12" rx="2"/>
      <line x1="9" y1="20" x2="15" y2="20"/>
      <line x1="12" y1="16" x2="12" y2="20"/>
    </svg>
  ),
  resolution: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <rect x="3" y="4" width="18" height="12" rx="2"/>
      <path d="M7 8h2v4H7zM12 8h2v4h-2z"/>
    </svg>
  ),
  touch: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <path d="M18 11V6a2 2 0 0 0-2-2 2 2 0 0 0-2 2v2"/>
      <path d="M14 10V4a2 2 0 0 0-2-2 2 2 0 0 0-2 2v2"/>
      <path d="M10 10.5V6a2 2 0 0 0-2-2 2 2 0 0 0-2 2v8"/>
      <path d="M18 11a2 2 0 1 1 4 0v3a8 8 0 0 1-8 8h-2a8 8 0 0 1-8-8 2 2 0 1 1 4 0"/>
    </svg>
  ),
  brightness: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <circle cx="12" cy="12" r="4"/>
      <line x1="12" y1="2" x2="12" y2="6"/>
      <line x1="12" y1="18" x2="12" y2="22"/>
      <line x1="2" y1="12" x2="6" y2="12"/>
      <line x1="18" y1="12" x2="22" y2="12"/>
      <line x1="4.93" y1="4.93" x2="7.76" y2="7.76"/>
      <line x1="16.24" y1="16.24" x2="19.07" y2="19.07"/>
      <line x1="4.93" y1="19.07" x2="7.76" y2="16.24"/>
      <line x1="16.24" y1="7.76" x2="19.07" y2="4.93"/>
    </svg>
  ),
  hdmi: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <rect x="2" y="7" width="20" height="10" rx="2"/>
      <path d="M7 7V5h10v2"/>
    </svg>
  ),
  panel: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <rect x="3" y="3" width="18" height="18" rx="3"/>
      <rect x="7" y="7" width="10" height="10" rx="1"/>
    </svg>
  ),
  viewing: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
      <circle cx="12" cy="12" r="3"/>
    </svg>
  ),
  led: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <path d="M15 14c.2-1 .7-1.7 1.5-2.5 1-.9 1.5-2.2 1.5-3.5A6 6 0 0 0 6 8c0 1 .2 2.2 1.5 3.5.7.7 1.3 1.5 1.5 2.5"/>
      <path d="M9 18h6"/>
      <path d="M10 22h4"/>
    </svg>
  ),
};



const Icon = ({ name }) => (
  <span className="pp-svg-icon">
    {svgIcons[name] || svgIcons.display}
  </span>
);


const Subtitle = ({ template, highlight }) => {
  const parts = template.split("{highlight}");
  return (
    <p className="pp-subtitle">
      {parts[0]}
      <strong>{highlight}</strong>
      {parts[1]}
    </p>
  );
};


const NotFound = ({ slug }) => (
  <div className="pp-not-found">
    <Helmet>
      <meta name="robots" content="noindex, follow" />
    </Helmet>
    <div className="pp-not-found-inner">
      <div className="pp-not-found-emoji">🔍</div>
      <h2>Product Not Found</h2>
      <p>No product with slug <code>"{slug}"</code> exists in <code>productsData.json</code>.</p>
      <p>Add a <code>"{slug}"</code> entry under <code>"products"</code> — the page appears instantly.</p>
    </div>
  </div>
);



const ProductPage = () => {

  const { slug } = useParams();

  const pageData = allProducts.products[slug];

  const [activeTab, setActiveTab] = useState("");
  const [activeImg, setActiveImg] = useState(0);

  useEffect(() => {
    if (pageData?.tabs?.length) {
      setActiveTab(pageData.tabs[0].key);
    }
    setActiveImg(0);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [slug, pageData]);


  const wrapperRef   = useRef(null);
  const imgRef       = useRef(null);
  const lensRef      = useRef(null);
  const resultRef    = useRef(null);
  const resultImgRef = useRef(null);

  useEffect(() => {
    const wrapper   = wrapperRef.current;
    const img       = imgRef.current;
    const lens      = lensRef.current;
    const result    = resultRef.current;
    const resultImg = resultImgRef.current;
    if (!wrapper || !img || !lens || !result || !resultImg) return;

    const updateZoom = () => {
      const cx = result.offsetWidth  / lens.offsetWidth;
      const cy = result.offsetHeight / lens.offsetHeight;
      resultImg.style.width  = img.offsetWidth  * cx + "px";
      resultImg.style.height = img.offsetHeight * cy + "px";
    };
    const moveLens = (e) => {
      const rect = wrapper.getBoundingClientRect();
      let x = e.clientX - rect.left  - lens.offsetWidth  / 2;
      let y = e.clientY - rect.top   - lens.offsetHeight / 2;
      x = Math.max(0, Math.min(x, wrapper.offsetWidth  - lens.offsetWidth));
      y = Math.max(0, Math.min(y, wrapper.offsetHeight - lens.offsetHeight));
      lens.style.left = x + "px";
      lens.style.top  = y + "px";
      const cx = result.offsetWidth  / lens.offsetWidth;
      const cy = result.offsetHeight / lens.offsetHeight;
      resultImg.style.left = -(x * cx) + "px";
      resultImg.style.top  = -(y * cy) + "px";
    };
    const onEnter = () => { lens.style.display = "block"; result.style.display = "block"; updateZoom(); };
    const onLeave = () => { lens.style.display = "none";  result.style.display = "none"; };

    wrapper.addEventListener("mousemove",  moveLens);
    wrapper.addEventListener("mouseenter", onEnter);
    wrapper.addEventListener("mouseleave", onLeave);
    window.addEventListener("resize",     updateZoom);
    return () => {
      wrapper.removeEventListener("mousemove",  moveLens);
      wrapper.removeEventListener("mouseenter", onEnter);
      wrapper.removeEventListener("mouseleave", onLeave);
      window.removeEventListener("resize",     updateZoom);
    };
  }, [activeImg, slug]);


  if (!pageData) return <NotFound slug={slug} />;

  const { product, images, specs, resources, parts, applications, tabs } = pageData;
  const currentImage = images[activeImg] || images[0];

  const plainTitle = product.title.replace(/\n/g, " ").replace(/\s+/g, " ").trim();
  const metaTitle = `${plainTitle} | BIBA Electronics`;
  const metaDescription = `${product.subtitle
    .replace("{highlight}", product.highlight)
    .replace(/\s+/g, " ")
    .trim()}`.slice(0, 160);

  return (
    <div className="pp-layout">
      <Helmet>
        <title>{metaTitle}</title>
        <meta name="description" content={metaDescription} />
        <meta property="og:title" content={metaTitle} />
        <meta property="og:description" content={metaDescription} />
        {currentImage?.src && <meta property="og:image" content={currentImage.src} />}
      </Helmet>
      <div className="pp-inner">

       
        <div className="pp-top-section">

          {/* LEFT: image gallery */}
          <div className="pp-image-col">
            <div className="pp-gallery">

              {/* Thumbnail strip — only when more than 1 image */}
              {images.length > 1 && (
                <div className="pp-gallery-thumbs">
                  {images.map((image, i) => (
                    <button
                      key={i}
                      className={`pp-thumb-btn ${activeImg === i ? "active" : ""}`}
                      onClick={() => setActiveImg(i)}
                      title={image.alt}
                    >
                      <img
                        src={image.src}
                        alt={image.alt}
                        onError={(e) => {
                          e.currentTarget.src = PLACEHOLDER_THUMB;
                          e.currentTarget.onerror = null;
                        }}
                      />
                    </button>
                  ))}
                </div>
              )}

              {/* Main zoomable image */}
              <div className="pp-gallery-main">
                <div className="pp-zoom-wrapper" ref={wrapperRef}>
                  <img
                    ref={imgRef}
                    key={`${slug}-${activeImg}`}
                    src={currentImage.src}
                    alt={currentImage.alt}
                    onError={(e) => {
                      e.currentTarget.src = PLACEHOLDER_MAIN;
                      e.currentTarget.onerror = null;
                    }}
                  />
                  <div className="pp-zoom-lens" ref={lensRef}></div>
                </div>
                <div className="pp-zoom-result" ref={resultRef}>
                  <img ref={resultImgRef} src={currentImage.src} alt="Zoom preview" />
                </div>
              </div>

            </div>
          </div>

          {/* RIGHT: product info */}
          <div className="pp-info-col">
            <div className="pp-badge">{product.badge}</div>

            <h1 className="pp-title">
              {product.title.split("\n").map((line, i, arr) => (
                <React.Fragment key={i}>
                  {line}
                  {i < arr.length - 1 && <br />}
                </React.Fragment>
              ))}
            </h1>

            <Subtitle template={product.subtitle} highlight={product.highlight} />

            <div className="pp-cta-row">
              <button className="pp-btn-primary">Request Quote</button>
              <a href={product.datasheetUrl} download className="pp-btn-outline">
                <DownloadIcon size={16} />
                Download Datasheet
              </a>
            </div>
          </div>
        </div>

        {/* ═══ TABS SECTION ═══════════════════════════════════════════════ */}
        <div className="pp-tabs-section">

          <div className="pp-tabs-header">
            {tabs.map((tab) => (
              <button
                key={tab.key}
                className={`pp-tab-btn ${activeTab === tab.key ? "active" : ""}`}
                onClick={() => setActiveTab(tab.key)}
              >
                <span className="pp-tab-emoji">{tab.emoji}</span>
                {tab.label}
              </button>
            ))}
          </div>

          {/* SPECIFICATIONS */}
          {activeTab === "specs" && (
            <div className="pp-tab-panel">
              <div className="pp-section-label">
                <span className="pp-chip">TECHNICAL SPECS</span>
                <h2 className="pp-section-title">Full Specifications</h2>
              </div>
              <div className="pp-specs-grid">
                {specs.map((s, i) => (
                  <div className="pp-spec-card" key={i}>
                    <div className="pp-spec-icon"><Icon name={s.icon} /></div>
                    <div className="pp-spec-label">{s.label}</div>
                    <div className="pp-spec-value">{s.value}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* RESOURCES */}
          {activeTab === "resources" && (
            <div className="pp-tab-panel">
              <div className="pp-section-label">
                <span className="pp-chip">DOWNLOADS</span>
                <h2 className="pp-section-title">Technical Resources</h2>
              </div>
              <div className="pp-feature-grid">
                {resources.map((r, i) => (
                  <a
                    key={i}
                    href={r.link}
                    download={r.link !== "#"}
                    className="pp-feature-card pp-feature-card--link"
                  >
                    <div className="pp-feature-emoji">{r.emoji}</div>
                    <div className="pp-feature-title">{r.title}</div>
                    <div className="pp-feature-desc">{r.desc}</div>
                    <div className="pp-feature-dl">Download →</div>
                  </a>
                ))}
              </div>
            </div>
          )}

          {/* PARTS */}
          {activeTab === "parts" && (
            <div className="pp-tab-panel">
              <div className="pp-section-label">
                <span className="pp-chip">COMPONENTS</span>
                <h2 className="pp-section-title">Parts List</h2>
              </div>
              <div className="pp-feature-grid">
                {parts.map((p, i) => (
                  <div className="pp-feature-card" key={i}>
                    <div className="pp-feature-emoji">{p.emoji}</div>
                    <div className="pp-feature-title">{p.title}</div>
                    <div className="pp-feature-desc">{p.desc}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* APPLICATIONS */}
          {activeTab === "applications" && (
            <div className="pp-tab-panel">
              <div className="pp-section-label">
                <span className="pp-chip">WHERE IT'S USED</span>
                <h2 className="pp-section-title">Applications</h2>
              </div>
              <div className="pp-feature-grid">
                {applications.map((a, i) => (
                  <div className="pp-feature-card" key={i}>
                    <div className="pp-feature-emoji">{a.emoji}</div>
                    <div className="pp-feature-title">{a.title}</div>
                    <div className="pp-feature-desc">{a.desc}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

        </div>
      </div>
    </div>
  );
};

export default ProductPage;  